import React from 'react';
import Immutable from 'immutable';
import ParentBoundComponent from '../../components/parentboundcomponent';
import FlexBox from '../../components/containers/flexbox'
import FlexBoxBlock from '../../components/containers/flexboxblock'
import Module from '../module';
import SearchModel from './searchmodel';
import Icon from '../../components/icon';
import ResultSetModule from '../resultset/resultsetmodule';
import styled, { withTheme } from 'styled-components';
import { getComponentForModuleId, processEntityLink } from '../moduleregistry';
import { getPhrase } from '../../data/phrases';
import { addEntityTimestampListener, getEntityTimestamp, removeEntityTimestampListener } from '../../data/entitytimestamps';
import { getLookup } from '../../data/lookupstore';
import memoizeOne from 'memoize-one';
import StaticLookup from '../../data/staticlookup';

const Wrapper = styled.div``;

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);

const getCustomProfileLookup = memoizeOne(list => {
	list = list || EMPTY_LIST;
	const items = Immutable.fromJS([{
		key: '',
		cells: [getPhrase('defaultProfile')]
	}]).concat(list.sort((a, b) => {return a.localeCompare(b)}).map(p => {
		return Immutable.Map({
			key: p,
			cells: [p]
		});
	}))
	return StaticLookup(items);
});

class SearchModule extends ParentBoundComponent {

	constructor(props) {
		super(props);

		this.search = this.search.bind(this);
		this.entityLinkRowAction = this.entityLinkRowAction.bind(this);
		this.onIconMouseDown = this.onIconMouseDown.bind(this);
		this.onSortRequested = this.onSortRequested.bind(this);
		this.onFixedColumnsChanged = this.onFixedColumnsChanged.bind(this);
		this.setOutput = this.setOutput.bind(this);
		this.setAdvancedFilter = this.setAdvancedFilter.bind(this);
		this.focusResult = this.focusResult.bind(this);
		this.entityTimestampListener = this.entityTimestampListener.bind(this);
		this.refresh = this.refresh.bind(this);
		this.getModel = this.getModel.bind(this);
		this.setModel = this.setModel.bind(this);
		this.setCustomFilter = this.setCustomFilter.bind(this);
		this.onSelectedProfileChanged = this.onSelectedProfileChanged.bind(this);
		this.deleteProfile = this.deleteProfile.bind(this);
		this.createProfile = this.createProfile.bind(this);
	}

	async deleteProfile(profile) {
		this.setModel(await this.getModel().deleteCustomProfile(profile));
	}

	async createProfile(name) {
		this.setModel(await this.getModel().createCustomProfile(name));
	}

	async onSelectedProfileChanged(value) {
		this.setModel(await this.getModel().setCustomProfile(value));
	}

	setCustomFilter(value) {
		this.setModel(this.getModel().setCustomFilter(value, true));
		window.setTimeout(() => {
			this.search(1);
		}, 0);
	}

	entityTimestampListener(entityName) {
		const data = this.getData();
		if (entityName === data.get('entityName')) {
			this.search();
		}
	}

	async setAdvancedFilter(filter) {
		let model = this.getModel().setAdvancedFilter(filter, true);
		model = model.setFirstRow(1);
		this.setModel(await model.search());
	}

	focusResult() {
		this.setModel(this.getModel().focusResult());
	}
	async setOutput(output) {
		let model = this.getModel().setOutput(output, true);
		this.setModel(await model.search());
	}

	onFixedColumnsChanged(value) {
		this.setData(this.getModel().setFixedColumns(value).getData());
	}


	async onSortRequested(columnName, reset) {
		let model = this.getModel().addToOrderBy(columnName, reset, true);
		model = await model.search();
		this.setData(model.getData());
	}

	entityLinkRowAction(row) {
		processEntityLink(this.getData().get('entityName'), row.get('id'));
	}

	getModel() {
		return new SearchModel(this.getData());
	}

	setModel(model) {
		this.setData(model.getData());
	}

	async search(firstRow, maxRowsPerPage) {
		let model = this.getModel();
		if (this.props.fixedFilter) {
			model = model.setFixedFilter(this.props.fixedFilter);
		}
		model = model.setFirstRow(firstRow || 1);
		if (maxRowsPerPage) {
			model = model.setMaxRowsPerPage(maxRowsPerPage, true);
		}
		model = await model.search(firstRow || 1);
		this.setData(model.getData());
	}

	async reload() {
		let model = this.getModel();
		model = await model.search();
		this.setData(model.getData());
	}

	async refresh() {
		const onBeforeRefresh = this.getData().get('onBeforeRefresh');
		if (typeof onBeforeRefresh === 'function') {
			const res = await onBeforeRefresh();
			if (res === false) {
				return;
			}
		}
		this.reload();
	}

	async componentDidMount() {
		let model = this.getModel();
		let formModel = model.getForm();
		if (formModel.initialize) {
			formModel = await formModel.initialize();
			model = model.setForm(formModel);
			this.setData(model.getData());
		}
		if (!model.hasBeenRun()) {
			model = model.initializePreferences();
			this.setModel(model);
			if (!model.getNoInitialSearch()) {
				this.search();
			}
		} else {
			model = await model.refreshIfEntityTimestampChanged();
			this.setData(model.getData());
		}
		addEntityTimestampListener(this.entityTimestampListener);
	}

	componentWillUnmount() {
		removeEntityTimestampListener(this.entityTimestampListener);
	}

	componentDidUpdate(prevProps) {
		const data = this.props.data;
		const prevData = prevProps.data;
		if (this.props.onSearch) {
			if (data.get('searchSeq') > (prevData.get('searchSeq') || 0)) {
				this.props.onSearch(this.getModel());
			}
		}
	}

	onIconMouseDown(row, iconId) {
		const imd = this.getProperty('onIconMouseDown');
		if (imd) {
			imd(this, row, iconId);
		}
	}

	renderResultSet(Result) {

	}

	render() {
		const data = this.getData();
		let onRowAction = data.get('onRowAction');
		let getRowClass;
		if (onRowAction) {
			getRowClass = getActionableRowClass;
		} else {
			onRowAction = this.props.onRowAction;
			getRowClass = this.props.getRowClass;
		}
		const entityName = data.get('entityName');
		if (!onRowAction) {
			const defaultRowAction = data.get('defaultRowAction');
			if (defaultRowAction) {
				if (defaultRowAction === 'entityLink') {
					if (entityName) {
						onRowAction = this.entityLinkRowAction;
					}
				}
			}
		}
		const result = data.get('result') || EMPTY_MAP;
		const csvExportCallback = data.get('csvExportCallback');
		const leftToolBar = this.props.leftToolBar || data.get('leftToolbar');
		let bottomRightToolbar = this.props.bottomRightToolbar || data.get('bottomRightToolbar');
		if (typeof bottomRightToolbar === 'function') {
			bottomRightToolbar = bottomRightToolbar(this, this.props.ctx);
		}
		const resultSetModuleId = data.get('resultSetModuleId');
		if (resultSetModuleId) {
			const CustomResult = getComponentForModuleId(resultSetModuleId);
			if (CustomResult) {
				return (
					<CustomResult
						data={data.getIn(['result', 'data'])}
						onDataChange={newData => {
							this.setData(data.setIn(['result', 'data'], newData));
						}}
						onPageRequested={this.search}
						onFixedColumnsChanged={this.onFixedColumnsChanged}
						onRowAction={onRowAction}
						onSortRequested={this.onSortRequested}
						getRowClass={getRowClass}
						focusTimestamp={data.get('resultFocusTimestamp')}
						onIconMouseDown={this.props.onResultIconMouseDown || this.onIconMouseDown}
						onFocusRequested={this.focusResult}
						setOutput={this.setOutput}
						setFilter={this.setAdvancedFilter}
						getSearchModel={this.getModel}
						customFilter={data.get('customFilter')}
						setCustomFilter={this.setCustomFilter}
						ctx={this.props.ctx}
						onNewEntityRequested={this.props.onNewEntityRequested}
						{...this.props.customResultProps}
					/>
				);
			}
		}
		return (
			<FlexBox vertical={!data.getIn(['form', 'data', 'vertical'])}>
				<FlexBoxBlock padding={this.props.theme.searchFormPadding}>
					<Module
						appState={data.get('form')}
						onDataChange={newData => {
							this.setData(data.setIn(['form', 'data'], newData));
						}}
						onSearchRequested={() => {
							this.search();
						}}
						ctx={this.props.ctx}
					/>
				</FlexBoxBlock>
				<FlexBoxBlock flexGrow="1" margin="8px 0px 0px 0px">
					<ResultSetModule
						data={data.getIn(['result', 'data'])}
						onDataChange={newData => {
							this.setData(data.setIn(['result', 'data'], newData));
						}}
						onPageRequested={this.search}
						onFixedColumnsChanged={this.onFixedColumnsChanged}
						onRowAction={onRowAction}
						onSortRequested={this.onSortRequested}
						getRowClass={getRowClass}
						focusTimestamp={data.get('resultFocusTimestamp')}
						onIconMouseDown={this.props.onResultIconMouseDown || this.onIconMouseDown}
						ctx={this.props.ctx}
						leftToolBar={
							leftToolBar ? (
								<Toolbar>{leftToolBar(this, this.props.ctx)}</Toolbar>
							) : null
						}
						onFocusRequested={this.focusResult}
						bottomRightToolbar={bottomRightToolbar}
						bottomMiddleToolbar={this.props.bottomMiddleToolbar}
						setOutput={this.setOutput}
						setFilter={this.setAdvancedFilter}
						pagerPrevButtonText={data.get('pagerPrevButtonText')}
						pagerNextButtonText={data.get('pagerNextButtonText')}
						pagerSpread={data.get('pagerSpread')}
						profileLookup={getCustomProfileLookup(data.get('customProfileList'))}
						selectedProfile={data.get('customProfile') || ''}
						onSelectedProfileChanged={this.onSelectedProfileChanged}
						onDeleteProfileRequested={this.deleteProfile}
						onCreateProfileRequested={this.createProfile}
						toolBar={
							(
								<Toolbar>
									<Icon
										name="refresh"
										className="standalone"
										active
										hint={getPhrase('refresh')}
										onMouseDown={this.refresh}
									/>
									{
										csvExportCallback && (
											<Icon
												name={csvExportCallback ? 'csv' : null}
												className="standalone"
												active
												hint={getPhrase('exportToCsv')}
												onMouseDown={e => {
													csvExportCallback(this.getModel());
												}}
											/>
										)
									}
								</Toolbar>
							)
						}
					/>
					{
						this.props.customOverlayContent
					}
				</FlexBoxBlock>
			</FlexBox>
		)
	}
}

function getActionableRowClass() {
	return 'actionable';
}

const Toolbar = styled.div`
	display: inline-block;
	margin-left: 8px;
	margin-right: 8px;

	.icon.standalone {
		font-size: 22px;
		color: ${props => props.theme.color};
		margin-right: 8px;
		&[data-name=refresh] {
			font-size: 16px;
		}
	}
`;


export default withTheme(SearchModule);
